'use client'
// NPCWorkforce — Command Center : état live des agents NPC (idle / executing + tâche).
// Consomme GET /api/game/npc-status. Auto-refresh 4 s. Brique n°2 du Mission Control.
import { useCallback, useEffect, useState } from 'react'
import { SectionLabel } from '@/components/landing/SectionLabel'

interface NpcAgent {
  id: string
  name: string
  emoji: string
  role: string
  model: string
  purposes: string[]
  capabilities: number
  status: 'idle' | 'executing' | string
  task: string | null
}

function AgentCard({ a }: { a: NpcAgent }) {
  const busy = a.status === 'executing'
  const color = busy ? 'var(--accent)' : 'var(--text-muted)'
  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      background: busy ? 'linear-gradient(180deg, rgba(79,124,255,0.07), rgba(0,0,0,0))' : 'var(--bg-card)',
      border: `1px solid ${busy ? 'rgba(79,124,255,0.3)' : 'var(--border-dim)'}`,
      borderRadius: 12, padding: '14px 16px',
    }}>
      {busy && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'var(--accent)' }} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>{a.emoji}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="font-display" style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {a.name}
          </div>
          <div style={{ fontSize: 9, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            {a.role} · {a.model}
          </div>
        </div>
        <span style={{
          flexShrink: 0, fontSize: 8, fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: '0.06em',
          color, background: `${color}14`, border: `1px solid ${color}38`, borderRadius: 8, padding: '2px 7px',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          {busy && <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--accent)', animation: 'pulse-dot 1.4s ease-in-out infinite' }} />}
          {busy ? 'EXEC' : 'IDLE'}
        </span>
      </div>

      {busy && a.task && (
        <div style={{ marginTop: 8, fontSize: 10, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          ▸ {a.task}
        </div>
      )}

      <div style={{ marginTop: 8, fontSize: 9, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
        {a.capabilities} Jinx · {a.purposes.slice(0, 2).join(', ')}
      </div>
    </div>
  )
}

export function NPCWorkforcePanel() {
  const [agents, setAgents] = useState<NpcAgent[]>([])
  const [activeCount, setActiveCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Carrousel : un agent à la fois, rotation auto 5 s (pause au survol),
  // les agents en exécution passent en tête de rotation.
  const [idx, setIdx] = useState(0)
  const [paused, setPaused] = useState(false)
  // Stable entre renders — le refresh data (4 s) ne doit pas réarmer le tick (5 s)
  const tick = useCallback(() => setIdx(i => i + 1), [])

  useEffect(() => {
    let alive = true
    const load = async () => {
      try {
        const r = await fetch('/api/game/npc-status')
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const d = await r.json()
        if (!alive) return
        setAgents(d.workforce ?? [])
        setActiveCount(d.activeCount ?? 0)
        setError(null)
      } catch (e: any) {
        if (alive) setError(e.message)
      } finally {
        if (alive) setLoading(false)
      }
    }
    load()
    const iv = setInterval(load, 4_000)
    return () => { alive = false; clearInterval(iv) }
  }, [])

  return (
    <section style={{ marginBottom: '48px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <SectionLabel>NPC Workforce</SectionLabel>
        <span style={{ marginLeft: 'auto', fontSize: 10, fontFamily: 'var(--font-mono)', color: activeCount > 0 ? 'var(--accent)' : 'var(--text-muted)' }}>
          {activeCount} exec · {agents.length} agent{agents.length > 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>Interrogation de l'équipe NPC…</p>
      ) : error ? (
        <p style={{ color: 'var(--coral)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>Indisponible : {error}</p>
      ) : agents.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>Aucun agent NPC.</p>
      ) : (() => {
        // Rotation : agents en exécution d'abord, puis les autres
        const ordered = [...agents].sort((a, b) => Number(b.status === 'executing') - Number(a.status === 'executing'))
        const current = ordered[idx % ordered.length]
        return (
          <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            <CarouselTicker length={ordered.length} paused={paused} onTick={tick} />
            <AgentCard a={current} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <button onClick={() => setIdx(i => (i - 1 + ordered.length) % ordered.length)} aria-label="Agent précédent"
                style={{ background: 'transparent', border: '1px solid var(--border-dim)', borderRadius: 6, color: 'var(--text-muted)', cursor: 'pointer', fontSize: 11, padding: '2px 9px' }}>‹</button>
              <div style={{ display: 'flex', gap: 5, flex: 1, justifyContent: 'center' }}>
                {ordered.map((a, i) => (
                  <span key={a.id} onClick={() => setIdx(i)} title={a.name}
                    style={{ width: 6, height: 6, borderRadius: '50%', cursor: 'pointer',
                      background: i === idx % ordered.length ? 'var(--accent)' : a.status === 'executing' ? 'rgba(79,124,255,0.45)' : 'rgba(255,255,255,0.15)' }} />
                ))}
              </div>
              <button onClick={() => setIdx(i => (i + 1) % ordered.length)} aria-label="Agent suivant"
                style={{ background: 'transparent', border: '1px solid var(--border-dim)', borderRadius: 6, color: 'var(--text-muted)', cursor: 'pointer', fontSize: 11, padding: '2px 9px' }}>›</button>
            </div>
          </div>
        )
      })()}
    </section>
  )
}

// Tick de rotation isolé — évite de réarmer l'intervalle à chaque render du parent.
function CarouselTicker({ length, paused, onTick }: { length: number; paused: boolean; onTick: () => void }) {
  useEffect(() => {
    if (paused || length < 2) return
    const iv = setInterval(onTick, 5_000)
    return () => clearInterval(iv)
  }, [paused, length, onTick])
  return null
}
